import { useEffect, useState, useCallback } from "react";
import api from "../api";

const POLL_MS = 20000;
const LAST_SEEN_KEY = "dsl.activity.lastSeen";

const TYPE_TAG = {
  "workstream.progress": { label: "Progress", cls: "tag-progress" },
  "workstream.deliverable": { label: "Deliverable", cls: "tag-deliv" },
  "comment.add": { label: "Comment", cls: "tag-comment" },
  "comment.state": { label: "Comment", cls: "tag-comment" },
  "task.add": { label: "Task", cls: "tag-task" },
  "task.done": { label: "Task", cls: "tag-task" },
  "leak.add": { label: "Leak", cls: "tag-leak" },
  "thread.add": { label: "Opportunity", cls: "tag-thread" },
  "thread.note": { label: "Thread note", cls: "tag-note" },
  "opportunity.add": { label: "Opportunity", cls: "tag-thread" },
  "milestone.add": { label: "Milestone", cls: "tag-milestone" },
  "question.add": { label: "Issue", cls: "tag-question" },
  "core.update": { label: "Core question", cls: "tag-core" },
  "discussion.topic.add": { label: "Discussion", cls: "tag-discussion" },
  "discussion.comment.add": { label: "Reply", cls: "tag-discussion" },
  "discussion.favorite": { label: "Starred", cls: "tag-starred" },
};

function relTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(0, Math.round((now - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d === 1) return "Yesterday";
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function buildSummary(ev) {
  const t = ev.target || {};
  if (ev.type === "workstream.progress") {
    return (
      <>
        <span className="actf-strong">
          Workstream {String(t.n).padStart(2, "0")} · {t.label}
        </span>{" "}
        <span className="actf-dim">{ev.summary}</span>
      </>
    );
  }
  if (ev.type === "workstream.deliverable") {
    return (
      <>
        <span className="actf-dim">on </span>
        <span className="actf-strong">
          Workstream {String(t.n).padStart(2, "0")}
        </span>{" "}
        <span className="actf-dim">— {ev.summary}</span>
      </>
    );
  }
  if (ev.type === "comment.add") {
    return (
      <>
        <span className="actf-dim">on </span>
        <span className="actf-strong">
          Workstream {String(t.n).padStart(2, "0")}
        </span>{" "}
        <span className="actf-quote">— &ldquo;{ev.summary}&rdquo;</span>
      </>
    );
  }
  if (ev.type === "comment.state") {
    return (
      <>
        <span className="actf-dim">{ev.summary} on </span>
        <span className="actf-strong">
          Workstream {String(t.n).padStart(2, "0")}
        </span>
      </>
    );
  }
  if (ev.type === "task.done" || ev.type === "task.add") {
    return <span className="actf-dim">{ev.summary}</span>;
  }
  if (ev.type === "thread.note") {
    return <span className="actf-dim">{ev.summary}</span>;
  }
  if (ev.type === "discussion.topic.add") {
    return (
      <>
        <span className="actf-dim">opened topic </span>
        <span className="actf-strong">&ldquo;{t.label}&rdquo;</span>
      </>
    );
  }
  if (ev.type === "discussion.comment.add" || ev.type === "discussion.favorite") {
    return <span className="actf-dim">{ev.summary}</span>;
  }
  return (
    <>
      <span className="actf-dim">{ev.summary}</span>
      {t.label && (
        <>
          {" "}
          <span className="actf-dim">·</span>{" "}
          <span className="actf-strong">{t.label}</span>
        </>
      )}
    </>
  );
}

export default function ActivityFeed({ limit = 10, onJump, embedded = false }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [lastSeen, setLastSeen] = useState(() => localStorage.getItem(LAST_SEEN_KEY) || "");
  const [unreadCount, setUnreadCount] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/activity?limit=${limit}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      const unread = (data.items || []).filter(
        (it) => !lastSeen || new Date(it.at).getTime() > new Date(lastSeen).getTime()
      ).length;
      setUnreadCount(unread);
    } catch (err) {
      // Background poll — never throw a transient network blip into the UI,
      // but surface it in dev so we don't lose signal.
      if (process.env.NODE_ENV === "development") {
        console.error("ActivityFeed poll failed:", err);
      }
    }
  }, [limit, lastSeen]);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const markAllSeen = () => {
    if (items.length === 0) return;
    const newest = items[0].at;
    localStorage.setItem(LAST_SEEN_KEY, newest);
    setLastSeen(newest);
    setUnreadCount(0);
  };

  const handleClick = (ev) => {
    markAllSeen();
    onJump?.(ev);
  };

  return (
    <aside
      className={`actf ${embedded ? "actf-embedded" : ""}`}
      data-testid="activity-feed"
    >
      <div className="actf-head">
        <h3>Recent activity</h3>
        <div className="actf-head-right">
          {unreadCount > 0 && (
            <span className="actf-badge" data-testid="activity-unread-badge">
              {unreadCount} new
            </span>
          )}
          <span className="actf-live">
            <span className="actf-dot" />
            Live
          </span>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="actf-empty">No activity yet. Edits will show up here.</div>
      ) : (
        items.map((ev) => {
          const tag = TYPE_TAG[ev.type] || { label: "Update", cls: "tag-other" };
          const isUnread = !lastSeen || new Date(ev.at).getTime() > new Date(lastSeen).getTime();
          return (
            <button
              type="button"
              className={`actf-row ${isUnread ? "unread" : ""}`}
              key={ev.id}
              onClick={() => handleClick(ev)}
              data-testid={`activity-row-${ev.id}`}
            >
              <span
                className={`actf-avatar actf-avatar-${ev.actor === "MH" ? "mh" : "wh"}`}
              >
                {ev.actor}
              </span>
              <span className="actf-body">
                <span className="actf-meta">
                  <span className="actf-who">{ev.actorName || (ev.actor === "MH" ? "Marco" : "Ward")}</span>
                  <span className="actf-sep">·</span>
                  <span>{relTime(ev.at)}</span>
                  {ev.count > 1 && (
                    <>
                      <span className="actf-sep">·</span>
                      <span
                        className="actf-count"
                        title={`${ev.count} updates collapsed within 5 minutes`}
                        data-testid={`activity-count-${ev.id}`}
                      >
                        ×{ev.count}
                      </span>
                    </>
                  )}
                  <span className="actf-sep">·</span>
                  <span className={`actf-tag ${tag.cls}`}>{tag.label}</span>
                </span>
                <span className="actf-summary">{buildSummary(ev)}</span>
              </span>
              <span className="actf-arrow">→</span>
            </button>
          );
        })
      )}

      {!embedded && total > items.length && (
        <button
          type="button"
          className="actf-foot"
          onClick={() => onJump?.({ type: "__history__" })}
          data-testid="activity-view-all"
        >
          View full history →
        </button>
      )}
    </aside>
  );
}
